import type {
  Config,
  NicknameToId,
  OnyxUpdatesFromServer,
  PendingOAuthCredential,
  Preferences,
  Profile,
  UserData,
} from '@src/types/onyx';
import type {Timestamp, UserID} from '@src/types/onyx/OnyxCommon';
import type {Auth, AuthCredential, User, UserCredential} from 'firebase/auth';
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  createUserWithEmailAndPassword,
  linkWithCredential,
  reauthenticateWithCredential,
  sendEmailVerification,
  signInWithCredential,
  signInWithEmailAndPassword,
  unlink,
  updatePassword as fbUpdatePassword,
  updateProfile,
  verifyBeforeUpdateEmail,
} from 'firebase/auth';
import {getOAuthCredential} from '@libs/OAuthCredential';
import * as Localize from '@libs/Localize';
import type {Timezone} from '@src/types/onyx/UserData';
import {getDeviceTimezone} from '@libs/TimezoneUtils';
import {validateAppVersion} from '@libs/Validation';
import type {OnyxEntry, OnyxUpdate} from 'react-native-onyx';
import Onyx from 'react-native-onyx';
import ONYXKEYS from '@src/ONYXKEYS';
import CONST from '@src/CONST';
import {getFirebaseAuth} from '@libs/Firebase/FirebaseApp';
import HttpsError from '@libs/Errors/HttpsError';
import * as API from '@libs/API';
import {
  READ_COMMANDS,
  SIDE_EFFECT_REQUEST_COMMANDS,
  WRITE_COMMANDS,
} from '@libs/API/types';
import type Response from '@src/types/onyx/Response';
import type {UserSearchResults} from '@src/types/various/Search';
import PusherUtils from '@libs/PusherUtils';
import * as Pusher from '@libs/Pusher/pusher';
import * as OnyxUpdates from '@userActions/OnyxUpdates';
import {fetchUserProfiles} from '@userActions/Profile';
import {PALETTES} from '@libs/SessionColorPalettes';
import Log from '@libs/Log';
import ERRORS from '@src/ERRORS';
import {isEmptyObject} from '@src/types/utils/EmptyObject';
import * as Session from './Session';

let verifyEmailSent: OnyxEntry<Timestamp | null> = null;
Onyx.connect({
  key: ONYXKEYS.VERIFY_EMAIL_SENT,
  callback: val => {
    verifyEmailSent = val;
  },
});

const getDefaultPreferences = (): Preferences => ({
  first_day_of_week: 'Monday',
  units_to_colors: {
    orange: 10,
    yellow: 5,
  },
  drinks_to_units: {
    small_beer: 0.5,
    beer: 1,
    cocktail: 1.5,
    other: 1,
    strong_shot: 1,
    weak_shot: 0.5,
    wine: 1,
  },
  theme: CONST.THEME.SYSTEM,
  session_color_palette: PALETTES.classic,
  use_own_palette_for_others: false,
});

const getDefaultUserData = (profileData: Profile): UserData => {
  const userRole = 'open_beta_user';
  const timezone: Timezone = {
    selected: getDeviceTimezone(),
    automatic: true,
  };
  return {
    profile: profileData,
    role: userRole,
    timezone,
  };
};

/**
 * Provision a brand-new user's backing data via kiroku-api. Replaces the former
 * direct RTDB write (`pushNewUserInfo`): the server owns the atomic creation of
 * `users/$uid`, `user_status/$uid`, `user_preferences/$uid`, and the
 * nickname-index tokens, keyed off the caller's Firebase ID token (so a new
 * user can only provision their OWN record) and 409-guarded so a replay can
 * never clobber an established account.
 *
 * Routed through `makeRequestWithSideEffects` rather than the fire-and-forget
 * `API.write` because the signup flow must observe success/failure to roll the
 * Firebase Auth user back on failure. The optimistic Onyx update mirrors the
 * server's `onyxData` so the post-auth routing gate can read the new user
 * immediately.
 *
 * @param userID The new user's Firebase uid — used only for the optimistic update
 * @param profileData Profile data of the user to create
 * @param options.applyOptimisticData Whether to optimistically write the default
 *   user data/preferences/theme into Onyx before the request. `true` (default)
 *   for the email/password signup flow, where the caller KNOWS the account is
 *   brand-new. The OAuth sign-in path passes `false`: there, provisioning is
 *   SPECULATIVE — it is attempted on every sign-in and a returning account is
 *   expected to come back `409`. Applying the optimistic defaults in that case
 *   would transiently overwrite a returning user's real `username_chosen` /
 *   preferences / theme (and, with their real account data not yet loaded, could
 *   mis-route them into onboarding) with no way to undo it: the `409` surfaces as
 *   a thrown HTTP rejection that never reaches `SaveResponseInOnyx`, so neither
 *   `onyxData` nor `failureData` is applied. A brand-new OAuth user is
 *   unaffected: `signInWithOAuth` writes the same defaults imperatively on the
 *   `200` success path (see `getNewUserOnyxData`), so the post-auth redirect
 *   does not depend on the server echoing `onyxData`.
 */
/**
 * The Onyx writes that seed a brand-new account's default user data,
 * preferences, and theme. Shared by `provisionUser`'s optimistic update (the
 * email/password path) and `signInWithOAuth`'s success path, which applies
 * them imperatively once the server confirms the account is genuinely new.
 */
function getNewUserOnyxData(
  userID: string,
  profileData: Profile,
): OnyxUpdate[] {
  const userData = getDefaultUserData(profileData);
  const preferences = getDefaultPreferences();
  return [
    {
      onyxMethod: Onyx.METHOD.MERGE,
      key: ONYXKEYS.USER_DATA_LIST,
      value: {[userID]: userData},
    },
    {
      onyxMethod: Onyx.METHOD.SET,
      key: ONYXKEYS.PREFERENCES,
      value: preferences,
    },
    {
      onyxMethod: Onyx.METHOD.SET,
      key: ONYXKEYS.PREFERRED_THEME,
      value: preferences.theme ?? CONST.THEME.SYSTEM,
    },
  ];
}

function provisionUser(
  userID: string,
  profileData: Profile,
  {applyOptimisticData = true}: {applyOptimisticData?: boolean} = {},
): Promise<void | Response> {
  const userData = getDefaultUserData(profileData);
  const preferences = getDefaultPreferences();

  // eslint-disable-next-line rulesdir/no-api-side-effects-method
  return API.makeRequestWithSideEffects(
    WRITE_COMMANDS.PROVISION_USER,
    {profile: profileData, timezone: userData.timezone, preferences},
    applyOptimisticData
      ? {optimisticData: getNewUserOnyxData(userID, profileData)}
      : {},
  );
}

/**
 * Presence heartbeat: ask the server to recompute the current user's
 * `user_status` from their own sessions. No client-supplied data is sent and
 * the server emits no `onyxData` (user_status has no top-level Onyx key).
 */
function syncUserStatus() {
  API.write(WRITE_COMMANDS.SYNC_USER_STATUS, {});
}

/**
 * Friend-search lookup: find users whose name matches `searchText`.
 *
 * Delegates to kiroku-api `GET /v1/users/search?q=`, which ports the former
 * direct-Firebase logic — tokenize the query, drive a single `nickname_to_id`
 * prefix-range query off the longest token, flatten the matches, and AND-refine
 * multi-word queries so every word must appear in the name. The matches come
 * back in the response's `searchResults` (a pure read — nothing is merged into
 * Onyx), so we read them straight off the response.
 *
 * @param searchText The text to search for.
 * @returns A Promise resolving to the matching user IDs.
 */
async function searchDatabaseForUsers(
  searchText: string,
): Promise<UserSearchResults> {
  if (!searchText.trim()) {
    return [];
  }
  // makeRequestWithSideEffects (not API.read) so the response is returned to us
  // and the matches can be read off it rather than merged into Onyx.
  // eslint-disable-next-line rulesdir/no-api-side-effects-method
  const response = await API.makeRequestWithSideEffects(
    READ_COMMANDS.SEARCH_USERS,
    {q: searchText},
  );
  const searchResults: NicknameToId = response?.searchResults ?? {};
  return Object.keys(searchResults);
}

/** Reauthentificate a user using the User object and a password
 * Necessary before important operations such as deleting a user
 * or changing a password.
 *
 * Return a promise with the credentials if the reauthentification succeeds,
 * or with null if it does not.
 *
 * @param user User object from firebase
 * @param password Password to reauthentificate with
 * @returns Null if the user does not exist, otherwise the result of the authentification.
 */
async function reauthentificateUser(
  user: User,
  password: string,
): Promise<void | UserCredential> {
  let email: string;
  if (user.email) {
    email = user.email;
  } else {
    Log.warn('User email not found when reauthentificating');
    return;
  }
  const credential = EmailAuthProvider.credential(email, password);
  const result = await reauthenticateWithCredential(user, credential);
  return result;
}

async function reauthenticateWithOAuth(
  user: User,
  providerId: string,
): Promise<UserCredential | null> {
  const getCredential = getOAuthCredential[providerId];
  if (!getCredential) {
    throw new Error(
      Localize.translateLocal('deleteAccountScreen.error.unsupportedProvider'),
    );
  }
  const credential = await getCredential();
  if (!credential) {
    return null;
  }
  return reauthenticateWithCredential(user, credential);
}

/**
 * Send an email to the user with a link to update their email.
 *
 * @param user The user to send the email to
 * @param newEmail The new email
 */
async function sendUpdateEmailLink(
  user: User | null,
  newEmail: string,
  password: string,
): Promise<void> {
  if (!user) {
    throw new Error(Localize.translateLocal('common.error.userNull'));
  }

  const authentificationResult: void | UserCredential =
    await reauthentificateUser(user, password);

  if (!authentificationResult) {
    throw new Error(
      Localize.translateLocal('common.error.reauthenticationFailed'),
    );
  }

  await verifyBeforeUpdateEmail(user, newEmail);
}

async function updatePassword(
  user: User | null,
  currentPassword: string,
  newPassword: string,
) {
  if (!user) {
    throw new Error(ERRORS.AUTH.USER_IS_NULL);
  }

  const authentificationResult: void | UserCredential =
    await reauthentificateUser(user, currentPassword);

  if (!authentificationResult) {
    throw new Error(
      Localize.translateLocal('common.error.reauthenticationFailed'),
    );
  }

  await fbUpdatePassword(user, newPassword);

  await Onyx.set(ONYXKEYS.FORMS.PASSWORD_FORM_DRAFT, null); // Reset upon success
}

/**
 * Send an email verification link to a user.
 *
 * @param user The user to send the email to
 * @param options Optional behavior overrides:
 *   - `bypassCooldown`: skip the per-device cooldown check. Used by `signUp` so
 *     the very first verification email always fires regardless of a prior
 *     user's send timestamp on the same device.
 * @returns A promise that resolves when the email is sent.
 */
async function sendVerifyEmailLink(
  user: User | null,
  options: {bypassCooldown?: boolean} = {},
): Promise<void> {
  if (!user) {
    throw new Error(Localize.translateLocal('common.error.userNull'));
  }
  if (!options.bypassCooldown) {
    const hasSentEmailRecently =
      verifyEmailSent &&
      verifyEmailSent > Date.now() - CONST.VERIFY_EMAIL.COOLDOWN;

    if (hasSentEmailRecently) {
      throw new Error(
        Localize.translateLocal('verifyEmailScreen.error.emailSentRecently'),
      );
    }
  }
  await sendEmailVerification(user);
  await Onyx.set(ONYXKEYS.VERIFY_EMAIL_SENT, new Date().getTime());
}

/**
 * Change a user's display name via kiroku-api, keeping the Firebase Auth
 * profile in sync. The server owns the nickname-index rebuild; the optimistic
 * Onyx update mirrors the server's `onyxData`.
 *
 * @param user User to change the display name for
 * @param newDisplayName The new display name
 */
function changeDisplayName(user: User | null, newDisplayName: string): void {
  if (!user) {
    return;
  }
  const optimisticData: OnyxUpdate[] = [
    {
      onyxMethod: Onyx.METHOD.MERGE,
      key: ONYXKEYS.USER_DATA_LIST,
      value: {[user.uid]: {profile: {display_name: newDisplayName}}},
    },
  ];
  API.write(
    WRITE_COMMANDS.UPDATE_DISPLAY_NAME,
    {displayName: newDisplayName},
    {optimisticData},
  );
  updateProfile(user, {displayName: newDisplayName}).catch(() => {
    // Auth profile sync is best-effort; the server write is authoritative.
  });
}

/**
 * Change a user's first/last name via kiroku-api. The optimistic Onyx update
 * mirrors the server's `onyxData`.
 *
 * @param user User to change the name for
 * @param firstName The new first name
 * @param lastName The new last name
 */
function changeUserName(
  user: User | null,
  firstName: string,
  lastName: string,
): void {
  if (!user) {
    return;
  }
  const optimisticData: OnyxUpdate[] = [
    {
      onyxMethod: Onyx.METHOD.MERGE,
      key: ONYXKEYS.USER_DATA_LIST,
      value: {
        [user.uid]: {profile: {first_name: firstName, last_name: lastName}},
      },
    },
  ];
  API.write(
    WRITE_COMMANDS.UPDATE_LEGAL_NAME,
    {firstName, lastName},
    {optimisticData},
  );
}

/**
 * Resolves the display names of an array of users, returning them as a mapping
 * object in the form `{[userId]: displayName}`.
 *
 * Reads through the batched kiroku-api endpoint
 * (`GET /v1/users/batch?fields=profile`, via `Profile.fetchUserProfiles`)
 * instead of the per-uid Firebase RTDB `users/$uid/profile` reads it used to
 * fire. A user whose profile the server omits (404 / denied) maps to an empty
 * string, preserving the previous reader's contract that every requested uid
 * gets an entry.
 *
 * @param userIds The users whose display names to resolve.
 * @returns A promise resolving to the nickname-by-id mapping.
 *
 * @example const nicknames = await fetchUserNicknames(["userUIDHere"]);
 */
async function fetchUserNicknames(userIds: UserID[]): Promise<NicknameToId> {
  if (isEmptyObject(userIds)) {
    return {};
  }

  const profiles = await fetchUserProfiles(userIds);

  return userIds.reduce((acc, userId) => {
    acc[userId] = profiles[userId]?.display_name ?? '';
    return acc;
  }, {} as NicknameToId);
}

/** Set the information about when the app update was last dimmissed */
async function setAppUpdateDismissed(timestamp: Timestamp): Promise<void> {
  await Onyx.merge(ONYXKEYS.APP_UPDATE_DISMISSED, timestamp);
}

/**
 * Persist the username the user chose on the post-auth username screen via
 * kiroku-api. The server owns the multi-path write — it renames
 * `profile.display_name`, rebuilds the `nickname_to_id` search index from the
 * name currently stored (so renames never leave stale tokens), and flips
 * `profile.username_chosen` in the SAME update so the routing gate can never
 * observe an inconsistent state. The optimistic Onyx update mirrors the
 * server's `onyxData`. The Firebase Auth profile is kept in sync separately
 * (best-effort); it is not RTDB state.
 *
 * @param user User choosing the username
 * @param newUsername The chosen username (also stored as the display name)
 */
function setUsername(user: User | null, newUsername: string): void {
  if (!user) {
    return;
  }
  const trimmed = newUsername.trim();
  const optimisticData: OnyxUpdate[] = [
    {
      onyxMethod: Onyx.METHOD.MERGE,
      key: ONYXKEYS.USER_DATA_LIST,
      value: {
        [user.uid]: {
          profile: {display_name: trimmed, username_chosen: true},
        },
      },
    },
  ];
  API.write(WRITE_COMMANDS.SET_USERNAME, {username: trimmed}, {optimisticData});
  updateProfile(user, {displayName: trimmed}).catch(() => {
    // Auth profile sync is best-effort; the server write is authoritative.
  });
}

/**
 * Sign in (or sign up) using a Firebase OAuth credential from Apple or Google.
 *
 * There is no longer a client-side existence pre-check (the last direct RTDB
 * read in this module): provisioning is attempted unconditionally and the server
 * is the authority. `provisionUser` creates a brand-new account; a returning
 * account is refused with `409` (the route is 409-guarded so a replay can never
 * clobber an established record), which we treat as "already provisioned" and
 * swallow. Any other failure propagates so the caller can surface it.
 *
 * @param auth The Firebase authentication object
 * @param credential The OAuth credential (OAuthProvider or GoogleAuthProvider)
 * @param displayName Optional display name captured from the OAuth provider response
 */
async function signInWithOAuth(
  auth: Auth,
  credential: AuthCredential,
  displayName?: string | null,
): Promise<void> {
  const userCredential = await signInWithCredential(auth, credential);
  const {user} = userCredential;
  const name =
    displayName ?? user.displayName ?? user.email?.split('@').at(0) ?? 'User';
  const profileData: Profile = {
    display_name: name,
    photo_url: user.photoURL ?? '',
    username_chosen: false,
  };
  // Drop any persisted record for this user before the new session bootstraps.
  // Sign-out's `cleanupSession` normally clears `USER_DATA_LIST`, but a
  // force-quit never unmounts `AuthScreens`, so a stale record (e.g. a
  // new-account skeleton with `username_chosen: false` and no onboarding/terms
  // stamps) can survive on disk into the next sign-in. Left in place, the
  // onboarding gate could read it as "needs onboarding" and strand a returning
  // user. `openApp` re-hydrates the authoritative record below; until it
  // succeeds, `USER_DATA_HYDRATED` keeps the gate shut.
  Onyx.merge(ONYXKEYS.USER_DATA_LIST, {[user.uid]: null});
  try {
    // Speculative: optimistic Onyx defaults are intentionally NOT applied (see
    // `provisionUser`) because a returning user reaches this on every sign-in
    // and the defaults would briefly overwrite their real data with no rollback.
    const response = await provisionUser(user.uid, profileData, {
      applyOptimisticData: false,
    });
    // The seed below must run for a CONFIRMED brand-new account ONLY — for a
    // returning user it would shadow their real record with new-account
    // defaults (`username_chosen: false`, no terms/onboarding stamps) and
    // mis-route them into onboarding. The `409` rejection jumping to the catch
    // is the primary gate, but resolution alone is NOT proof of creation: a
    // field repro (2026-06-11, dev) showed an already-provisioned account
    // getting seeded after the server returned a real HTTP 409, i.e. some
    // layer turned the rejection into a resolution. So require the positive
    // creation signal — the server's own `jsonCode === 200` envelope — before
    // seeding, and treat anything else as "already provisioned".
    const jsonCode = (response as Response | undefined)?.jsonCode;
    if (jsonCode === 200) {
      // Seed the default user data into Onyx now that the `200` has confirmed a
      // genuinely new account, mirroring the synchronous optimistic write the
      // email/password signup already does. The post-auth OnboardingGuard reads
      // the user's record to route a brand-new account into onboarding; seeding
      // here makes that record available locally rather than depending solely on
      // the server's `onyxData` echo racing `openApp`.
      await Onyx.update(getNewUserOnyxData(user.uid, profileData));
      await updateProfile(user, {displayName: name});
    } else {
      // Any non-200 resolution is treated as "already provisioned": a returning
      // user whose record must NOT be shadowed with new-account defaults. This is
      // intentionally defensive — a `409` normally rejects into the catch below,
      // but a 2026-06-11 field repro showed an already-provisioned account being
      // seeded after a real HTTP 409, i.e. some transport layer resolving the
      // rejection. Gating the seed on the positive `jsonCode === 200` creation
      // signal closes that hole regardless of which layer swallowed the rejection.
      Log.info(
        '[signInWithOAuth] provisioning resolved without a 200 creation signal; skipping new-user seed',
        false,
        {jsonCode: String(jsonCode)},
      );
    }
  } catch (error) {
    // `409 Conflict` = the account already exists (returning user). That is the
    // expected replay outcome — proceed to sign-in. Everything else is a real
    // provisioning failure and must propagate.
    if (
      !(error instanceof HttpsError) ||
      error.status !== String(CONST.HTTP_STATUS.CONFLICT)
    ) {
      throw error;
    }
    // A `409 Conflict` is the expected returning-user replay outcome: the
    // account already exists, so no seed runs and we fall through to sign-in.
  }
  Session.clearSignInData();
  // Post-auth routing is owned by OnboardingGuard. Navigating here would
  // race with the guard's redirect to onboarding/terms for accounts that
  // still owe the flow (i.e. a fresh OAuth signup whose Firebase record was
  // just created with `username_chosen: false`).
}

/**
 * Stash the raw OAuth materials when Firebase reports that the email already
 * belongs to a different account (`auth/account-exists-with-different-credential`).
 * The caller extracts the idToken/rawNonce/displayName from the OAuth response;
 * this helper extracts the existing-account email from the FirebaseError and
 * writes the combined record so the collision-resolution modal can pick it up.
 *
 * Returns true if the stash was written, false if the error did not carry an
 * email (caller should fall back to the generic error path).
 */
function stashPendingOAuthCredential(
  error: unknown,
  data: {
    providerId: PendingOAuthCredential['providerId'];
    idToken: string;
    rawNonce?: string;
    displayName?: string | null;
  },
): boolean {
  const email = (error as {customData?: {email?: string}}).customData?.email;
  if (!email) {
    return false;
  }
  Onyx.set(ONYXKEYS.PENDING_OAUTH_CREDENTIAL, {email, ...data});
  return true;
}

/** Drop the stashed OAuth credential — call on cancel, successful link, or unrecoverable error. */
function clearPendingOAuthCredential(): void {
  Onyx.set(ONYXKEYS.PENDING_OAUTH_CREDENTIAL, null);
}

/**
 * Resolve an OAuth sign-in collision by signing the user in with their existing
 * email/password account and linking the pending OAuth credential to it. After
 * this completes, both methods sign into the same Firebase account.
 *
 * The caller passes in the pending credential (read via `useOnyx` in the
 * collision modal) so this action stays pure and doesn't depend on module
 * state.
 *
 * - Wrong password → throws Firebase's wrong-password error; the caller should
 *   keep the pending credential stash so the user can retry.
 * - linkWithCredential failure → clears the stash and re-throws; the user must
 *   re-OAuth because the token is likely stale.
 */
async function linkPendingOAuthCredential(
  auth: Auth,
  password: string,
  pending: PendingOAuthCredential,
): Promise<void> {
  const userCredential = await signInWithEmailAndPassword(
    auth,
    pending.email,
    password,
  );

  const credential: AuthCredential =
    pending.providerId === 'apple.com'
      ? new OAuthProvider('apple.com').credential({
          idToken: pending.idToken,
          rawNonce: pending.rawNonce,
        })
      : GoogleAuthProvider.credential(pending.idToken);

  try {
    await linkWithCredential(userCredential.user, credential);
  } catch (linkError) {
    // Stale/invalid token — drop the stash; the user must re-OAuth.
    clearPendingOAuthCredential();
    throw linkError;
  }

  clearPendingOAuthCredential();
  Session.clearSignInData();
}

/**
 * Link an OAuth credential to the currently signed-in user. This is the
 * "Connected Accounts" / proactive path — distinct from
 * `linkPendingOAuthCredential`, which is the reactive sign-in-collision path
 * that first signs the user in with email/password.
 *
 * After a successful link, `auth.currentUser.providerData` is refreshed so
 * callers can re-render the linked-providers list without a separate reload.
 *
 * Common errors the caller should handle:
 * - `auth/credential-already-in-use` / `auth/email-already-in-use` — the OAuth
 *   identity is already attached to a different Firebase account. We do not
 *   auto-merge; the caller should show a localized error.
 * - `auth/requires-recent-login` — sign-in is too old; the caller should
 *   reauthenticate and retry.
 */
async function linkOAuthProvider(
  auth: Auth,
  credential: AuthCredential,
): Promise<void> {
  if (!auth.currentUser) {
    throw new Error(ERRORS.AUTH.USER_IS_NULL);
  }
  await linkWithCredential(auth.currentUser, credential);
  // providerData is not auto-refreshed after link/unlink — reload so the
  // caller sees the new provider list immediately.
  await auth.currentUser.reload();
}

/**
 * Unlink a provider (Apple/Google, or `password`) from the currently signed-in
 * user. Refuses to unlink the last remaining provider — that would lock the
 * user out of their account.
 *
 * Common errors the caller should handle:
 * - `auth/requires-recent-login` — sign-in is too old; the caller should
 *   reauthenticate and retry.
 * - `auth/no-such-provider` — the provider isn't linked (should not happen if
 *   the UI is driven off `providerData`, but defensive).
 */
async function unlinkOAuthProvider(
  auth: Auth,
  providerId: string,
): Promise<void> {
  if (!auth.currentUser) {
    throw new Error(ERRORS.AUTH.USER_IS_NULL);
  }
  if (auth.currentUser.providerData.length <= 1) {
    throw new Error(ERRORS.AUTH.LAST_PROVIDER);
  }
  await unlink(auth.currentUser, providerId);
  await auth.currentUser.reload();
}

/** Attempt to log in to the Firebase authentication service with a user's credentials
 *
 * @param auth The Firebase authentication object
 * @param email The user's email
 * @param password The user's password
 */
async function logIn(
  auth: Auth,
  email: string,
  password: string,
): Promise<void> {
  await signInWithEmailAndPassword(auth, email, password);
}

/**
 * Sign up a user to the authentication service using an email and a password.
 * The username is captured separately on the post-auth username screen, so a
 * placeholder display_name is derived from the email and `username_chosen` is
 * set to `false` to gate the rest of the app behind that screen.
 *
 * @param auth The Firebase authentication object
 * @param email The new user's email
 * @param password The new user's password
 */
async function signUp(
  auth: Auth,
  email: string,
  password: string,
): Promise<void> {
  // Gate signup on the minimum account-creation version. This runs BEFORE the
  // Firebase Auth account (and therefore any ID token) exists, so it goes out as
  // a PUBLIC, unauthenticated request: `GetMinVersion` is registered in
  // `kirokuRoutes` with `requiresAuth: false`, so the pipeline sends it without
  // a Bearer header. `makeRequestWithSideEffects` returns the body so we can
  // read the gate synchronously before creating the account.
  let minUserCreationVersion: string | null = null;
  try {
    // eslint-disable-next-line rulesdir/no-api-side-effects-method
    const response = await API.makeRequestWithSideEffects(
      SIDE_EFFECT_REQUEST_COMMANDS.GET_MIN_VERSION,
      {},
    );
    const data = response as unknown as
      | {min_user_creation_possible_version?: string | null}
      | undefined;
    minUserCreationVersion = data?.min_user_creation_possible_version ?? null;
  } catch {
    // Network failure or a non-OK response. Reuse the existing gate error key
    // so the AuthScreen copy still maps.
    throw new Error('database/data-fetch-failed');
  }

  // A `null` gate means "no minimum" — the server returns null when the node is
  // absent, which it treats as "no restriction". Only enforce the version check
  // when the server returns an actual minimum version.
  if (
    minUserCreationVersion &&
    !validateAppVersion({min_supported_version: minUserCreationVersion}).success
  ) {
    throw new Error('database/outdated-app-version');
  }

  // Derive a placeholder display name from the email so the username screen
  // has something to prefill. The user confirms or replaces it before exiting.
  const derivedName = email.split('@').at(0) ?? 'User';
  const newProfileData: Profile = {
    display_name: derivedName,
    photo_url: '',
    username_chosen: false,
  };

  // Create the user in the Firebase authentication. Firebase Auth enforces
  // its own abuse rate limit and returns `auth/too-many-requests` once
  // tripped — rethrow as the dedicated account-creation key so the UI can
  // surface the existing "rate limit exceeded for account creation" copy
  // instead of the generic too-many-requests message.
  let userCredential: UserCredential;
  try {
    userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password,
    );
  } catch (error) {
    const code =
      typeof error === 'object' && error !== null && 'code' in error
        ? (error as {code?: unknown}).code
        : undefined;
    if (code === ERRORS.AUTH.TOO_MANY_REQUESTS) {
      throw new Error(ERRORS.AUTH.ACCOUNT_CREATION_LIMIT_EXCEEDED);
    }
    throw error;
  }
  const newUser = userCredential.user;
  const newUserID = newUser.uid;

  // Fire-and-forget the verification email. Without this, the account's
  // email stays unverified — and Firebase's "single account per email"
  // policy will then silently OVERWRITE the password provider if the same
  // user later signs in via Apple/Google with the same email (because
  // OAuth verifies the email and Firebase trusts the verified claim over
  // the unverified password account). The takeover is irreversible: the
  // user gets locked out of email/password sign-in with no error. Sending
  // verification immediately on signup means the collision path goes
  // through `auth/account-exists-with-different-credential` instead and
  // triggers the OAuthLinkModal. We bypass the cooldown because this is
  // the canonical first send for a brand-new account; the cooldown is
  // device-global and otherwise blocks first-sends after a different user
  // recently sent on this device. Routing through the wrapper (instead of
  // the bare Firebase call) ensures `VERIFY_EMAIL_SENT` is written so the
  // VerifyEmailModal opens in the truthful "Check your inbox" state.
  sendVerifyEmailLink(newUser, {bypassCooldown: true}).catch(error => {
    Log.alert('[signUp] failed to send verification email', {
      error,
    });
  });

  try {
    // Set the Auth profile display name BEFORE provisioning so the rollback
    // path below is always orphan-free: the only RTDB write (provisionUser) is
    // the last statement, so if anything in this block throws there is no
    // half-created RTDB graph to clean up — deleting the Auth user suffices.
    await updateProfile(newUser, {displayName: derivedName});

    // Provision the user's backing data server-side (kiroku-api owns the atomic
    // RTDB creation). Awaited so a failure can roll the Auth user back below.
    await provisionUser(newUserID, newProfileData);

    Session.clearSignInData();

    // Post-auth routing is owned by OnboardingGuard. Navigating here would
    // race with the guard's redirect to onboarding/terms and the guard would
    // lose (its useEffect runs after this synchronous nav, then its own
    // lastTargetRef debounce blocks any retry).
  } catch (error) {
    // Provisioning is atomic + 409-guarded server-side, so a failure leaves no
    // partial RTDB graph to clean up — only the just-created Firebase Auth user
    // needs rolling back, which frees the claimed email for a retry. The delete
    // is best-effort; surface the canonical creation-failed error regardless.
    await newUser.delete().catch(deleteError => {
      Log.alert('[signUp] failed to roll back Firebase Auth user', {
        error: deleteError,
      });
    });
    throw new Error('database/user-creation-failed');
  }
}

/** The flat payload kiroku-api pushes on the `onyxApiUpdate` event. */
type KirokuOnyxApiUpdateEvent = {
  eventType: string;
  data: OnyxUpdate[];
  lastUpdateID: number;
  previousUpdateID: number;
};

/**
 * Subscribe the signed-in user to their realtime channel. kiroku-api pushes an
 * `onyxApiUpdate` event on `private-user-<uid>` carrying an `OnyxUpdate[]`; we
 * apply it to Onyx through the standard OnyxUpdates pipeline.
 */
function subscribeToUserEvents() {
  const userID = getFirebaseAuth().currentUser?.uid;
  if (!userID) {
    return;
  }

  // The handler that applies an `onyxApiUpdate`'s OnyxUpdate[] to Onyx.
  PusherUtils.subscribeToMultiEvent(
    Pusher.TYPE.MULTIPLE_EVENT_TYPE.ONYX_API_UPDATE,
    (updates: OnyxUpdate[]) => Onyx.update(updates),
  );

  PusherUtils.subscribeToPrivateUserChannelEvent(
    Pusher.TYPE.ONYX_API_UPDATE,
    userID,
    pushJSON => {
      const event = pushJSON as unknown as KirokuOnyxApiUpdateEvent;
      const previousUpdateID = Number(event.previousUpdateID ?? 0);
      const updateParams: OnyxUpdatesFromServer = {
        type: CONST.ONYX_UPDATE_TYPES.PUSHER,
        lastUpdateID: Number(event.lastUpdateID ?? 0),
        previousUpdateID,
        updates: [{eventType: event.eventType, data: event.data}],
      };

      // If the client missed updates (e.g. pushes that arrived while offline),
      // there is a gap between previousUpdateID and what we've applied. Defer this
      // update and let OnyxUpdateManager backfill the gap via GET /v1/updates,
      // then apply the deferred updates in order. Otherwise apply it directly.
      if (OnyxUpdates.doesClientNeedToBeUpdated(previousUpdateID)) {
        OnyxUpdates.saveUpdateInformation(updateParams);
        return;
      }

      OnyxUpdates.apply(updateParams);
    },
  );
}

/**
 * Subscribe to global app-config broadcasts. kiroku-api publishes the full
 * `Config` object on the public `config` channel (event `configUpdate`). Config
 * is global + last-write-wins, so we idempotently overwrite the Onyx key on each
 * push rather than routing it through the per-user OnyxUpdates gap-detection
 * pipeline (that's only for `private-user-<uid>`). `app/open` seeds the baseline;
 * this keeps it live.
 */
function subscribeToConfigEvents() {
  PusherUtils.subscribeToPublicChannelEvent<Config>(
    CONST.PUSHER.CONFIG_CHANNEL,
    CONST.PUSHER.CONFIG_UPDATE_EVENT,
    config => {
      Onyx.set(ONYXKEYS.CONFIG, config);
    },
  );
}

export {
  subscribeToUserEvents,
  subscribeToConfigEvents,
  changeDisplayName,
  changeUserName,
  fetchUserNicknames,
  searchDatabaseForUsers,
  getDefaultPreferences,
  getDefaultUserData,
  reauthentificateUser,
  reauthenticateWithOAuth,
  sendUpdateEmailLink,
  sendVerifyEmailLink,
  setAppUpdateDismissed,
  setUsername,
  syncUserStatus,
  updatePassword,
  logIn,
  signInWithOAuth,
  stashPendingOAuthCredential,
  clearPendingOAuthCredential,
  linkPendingOAuthCredential,
  linkOAuthProvider,
  unlinkOAuthProvider,
  signUp,
};
